
import { getPrettyCurrentDate } from 'bits/date'
import { isAppUsing } from 'bits/flip'
import { currentUrl, transformCurrentUrl } from 'bits/location'
import { Component, Prop, Vue, namespace } from 'bits/vue'
import OzBox, { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzCloseButton from 'library/v4/components/OzCloseButton.vue'
import OzContainedButton from 'library/v4/components/OzContainedButton.vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzIndeterminateSpinner from 'library/v4/components/OzIndeterminateSpinner.vue'
import OzScreenSizeAwareCenterModal from 'library/v4/components/OzScreenSizeAwareCenterModal.vue'
import type { AdvertisedQuotasResult, EstimateSwitchingCostResult, User } from 'types'
import BillingCollectionItems from 'vuecomponents/BillingCollectionItems.vue'
import DashboardSettingsBillingModalTitle from 'vuecomponents/DashboardSettingsBillingModalTitle.vue'
import GoldMembershipTier, { Variant as GoldMembershipTierVariant } from 'vuecomponents/GoldMembershipTier.vue'
import MembershipTierActionButton from 'vuecomponents/MembershipTierActionButton.vue'
import NeonMembershipTier, { Variant as NeonMembershipTierVariant } from 'vuecomponents/NeonMembershipTier.vue'
import PaymentScheduleTier, { PaymentSchedule, Tier } from 'vuecomponents/PaymentScheduleTier.vue'
import PlatinumMembershipTier, {
  Variant as PlatinumMembershipTierVariant,
} from 'vuecomponents/PlatinumMembershipTier.vue'
import { UpgradeSource, UpgradeStep } from 'vuexstore/modules/plans'

const browser = namespace('browser')
const plans = namespace('plans')

@Component({
  components: {
    BillingCollectionItems,
    DashboardSettingsBillingModalTitle,
    GoldMembershipTier,
    MembershipTierActionButton,
    NeonMembershipTier,
    OzBox,
    OzCloseButton,
    OzContainedButton,
    OzDivider,
    OzIndeterminateSpinner,
    OzScreenSizeAwareCenterModal,
    PaymentScheduleTier,
    PlatinumMembershipTier,
  },
})
export default class MembershipTierUpgradeModal extends Vue {
  @browser.Getter isSmallerThanTabletPortrait!: boolean
  @browser.Getter windowHeight!: number
  @plans.Getter estimatedUpgradeCost!: EstimateSwitchingCostResult
  @plans.Getter isProcessingUpgrade!: boolean
  @plans.Getter plans
  @plans.Getter plansFetched!: boolean
  @plans.Getter advertisedQuotas!: AdvertisedQuotasResult
  @plans.Getter isLoadingAdvertisedQuotas!: boolean
  @plans.Getter upgradeStep!: UpgradeStep
  @plans.Getter upgradeError!: string
  @plans.Getter upgradeSource!: UpgradeSource | null
  @plans.Getter hasUserTriggeredUpgrade!: boolean
  @plans.Getter estimatedCancellationRefundAmount!: string
  @plans.Getter isLoadingCancellationRefundInformation!: boolean

  @Prop() user!: User
  @Prop({ required: false }) fileThatExceededSize

  @plans.Action upgradeToGold
  @plans.Action upgradeToGoldDifferentCurrency
  @plans.Action upgradeToPlatinum
  @plans.Action upgradeToPlatinumDifferentCurrency
  @plans.Action showUpgradeToPlatinumModalCheckingScheduledChanges
  @plans.Action showPlanUpgradeToGoldModal
  currentDate = getPrettyCurrentDate()

  isAppUsing = isAppUsing
  OzBoxColors = OzBoxColors
  OzDividerColorScheme = OzDividerColorScheme
  PaymentSchedule = PaymentSchedule
  Tier = Tier
  UpgradeStep = UpgradeStep
  GoldMembershipTierVariant = GoldMembershipTierVariant
  NeonMembershipTierVariant = NeonMembershipTierVariant
  PlatinumMembershipTierVariant = PlatinumMembershipTierVariant

  get padletQuotaNeonMembershipTierVariant(): NeonMembershipTierVariant {
    if (this.user.quota.walls_used > this.advertisedQuotas.neon.max_walls!)
      return NeonMembershipTierVariant.OverPadletQuota
    if (this.user.quota.walls_used === this.advertisedQuotas.neon.max_walls)
      return NeonMembershipTierVariant.AtPadletQuota
    return NeonMembershipTierVariant.UnderPadletQuota
  }

  get padletQuotaGoldMembershipTierVariant(): GoldMembershipTierVariant {
    if (this.user.membership_tier === 'gold') {
      if (this.user.quota.walls_used > this.advertisedQuotas.gold.max_walls!)
        return GoldMembershipTierVariant.OverPadletQuota
      if (this.user.quota.walls_used === this.advertisedQuotas.gold.max_walls)
        return GoldMembershipTierVariant.AtPadletQuota
      return GoldMembershipTierVariant.UnderPadletQuota
    }
    if (this.user.quota.walls_used > 20) return GoldMembershipTierVariant.OverPadletQuota
    return GoldMembershipTierVariant.Upgrade
  }

  get fileSizeQuotaGoldMembershipTierVariant(): GoldMembershipTierVariant {
    if (
      this.user.membership_tier === 'gold' ||
      this.fileThatExceededSize.size > this.advertisedQuotas.gold.max_upload_megabytes! * 1000000
    )
      return GoldMembershipTierVariant.FileSize
    return GoldMembershipTierVariant.Upgrade
  }

  get membershipConfirmationPageUrl(): string {
    return transformCurrentUrl({}, { path: '/memberships/confirmation', search: { next: currentUrl() } })
  }
}
